import { NotFoundError } from '@cian/peperrors';

import {
  fetchChatsUnmarkAsStarred,
  TChatsUnmarkAsStarredRequest,
  TChatsUnmarkAsStarredResponse,
} from '../../repositories/chats/v1/chats/unmark-as-starred';
import { showAlert } from '../../slices/alertSlice';
import { EAlert } from '../../types/alert';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { getResponseError } from '../../utils/logging/getResponseError';
import { deselectAllChats, toggleChatListPending, updatedFromServer } from '../chatList/actions';

const DEFAULT_ERROR_MESSAGE = 'Не удалось чат убрать из важных';

/**
 * Пометить чат как не важный
 * @param parameters.chatId id чата
 */
export const unmarkChatAsStarred =
  (parameters: TChatsUnmarkAsStarredRequest): TThunkAction<Promise<void>> =>
  async (dispatch, _getState, context) => {
    const { httpApi, logger, telemetry } = context;

    dispatch(toggleChatListPending(true));

    try {
      const { statusCode, response }: TChatsUnmarkAsStarredResponse = await fetchChatsUnmarkAsStarred({
        httpApi,
        config: { requestConfig: { withCredentials: true } },
        parameters: { chatId: parameters.chatId },
      });

      if (statusCode !== 200) {
        throw getResponseError({
          domain: EExceptionDomain.Chat,
          response,
          defaultMessage: DEFAULT_ERROR_MESSAGE,
          statusCode,
        });
      }

      if (!response.chat) {
        throw new NotFoundError({
          domain: EExceptionDomain.Chat,
          message: 'Чат для удаления из важных не найден',
          details: {
            chatId: parameters.chatId,
          },
        });
      }

      dispatch(updatedFromServer(response.chat));
      telemetry.increment(ETelemetryEvents.UnmarkMessagesAsStarredSuccess);
      dispatch(deselectAllChats());

      /**
       * При успешном помечании чата как важного, показываем алерт
       */
      dispatch(showAlert({ type: EAlert.Info, message: 'Чат удален из важных' }));
    } catch (ex) {
      telemetry.increment(ETelemetryEvents.UnmarkMessagesAsStarredFail);

      const exception = getGenericLikeError({
        error: ex,
        domain: EExceptionDomain.Chat,
        message: DEFAULT_ERROR_MESSAGE,
      });

      logger.error(exception, {
        microserviceName: 'chats',
        pathApi: '/v1/chats/unmark-as-starred/',
        parameters: JSON.stringify({ chatId: parameters.chatId }),
      });

      dispatch(showAlert({ message: DEFAULT_ERROR_MESSAGE }));
    } finally {
      dispatch(toggleChatListPending(false));
    }
  };
